import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import { actions, getters } from "./model";
import { SECTIONS_IDS } from "./constants";
import { push } from "react-router-redux";

import { eventDetails as getEventDetails } from "redux/modules/portal/selectors";
import { registerError } from "redux/modules/errors/actions";
import { apiCall } from "App/Data/sagas";

import { actions as SessionFlyOverActions } from "Portal/NewPortal/Sections/Sessions/FlyOver/model";
import { actions as TaskFlyOverActions } from "Portal/NewPortal/Sections/Home/Cards/Tasks/FlyOver/model";

import { getActiveAccountId } from "../HeaderBar/selectors";
import { userId as getUserId } from "redux/modules/user/selectors";

import { isEmptyOrNil } from "utils/General";

import { getAllTasks } from "Portal/NewPortal/Sections/Home/Cards/Tasks/selectors";

const refreshData = function*(payload = {}) {
  try {
    const eventDetails = yield select(getEventDetails);
    const activeAccountId = yield select(getActiveAccountId);

    const { id: eventId = 0 } = eventDetails;

    const { payload: portalPayload } = yield call(apiCall, {
      method: "get",
      url: `/portal/tasks/event/${eventId}${
        activeAccountId ? `/accounts/${activeAccountId}` : ""
      }`,
      qs: {
        scope: "home"
      }
    });

    yield all([
      put(
        actions.setInitialData({
          ...portalPayload,
          sessionsTasks: portalPayload.session_tasks
        })
      )
    ]);

    // reload sidebar if details specified
    const { payload: payloadTaskFlyover } = payload || {};
    const { taskData, taskSessionId } = payloadTaskFlyover || {};

    if (!isEmptyOrNil(taskData)) {
      const tasks = yield select(getAllTasks);
      let newTaskData = taskData;
      if (taskSessionId) {
        const session = tasks.find(task => task.id === taskSessionId);
        newTaskData = session.tasks.find(task => task.id === taskData.id);
      } else {
        newTaskData = tasks.find(task => task.id === taskData.id);
      }

      yield put(
        TaskFlyOverActions.openTaskFlyOver({
          taskData: newTaskData,
          taskSessionId
        })
      );
    }
  } catch (e) {
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred retrieving getPortalData"
        }
      ])
    );
  }
};

/**
 * Taking a legacy getPortalUser function, we hydrate the global store with the user data
 */
const refreshUserData = function*({ legacyGetPortalUserFn }) {
  try {
    const eventDetails = yield select(getEventDetails);
    const userId = yield select(getUserId);

    // @NOTE: We prop-drill a legact get Portal User fn so we can hydrate the
    // global store and minimize refactoring. Ideally we move toward migrating this data to other global store.
    const user = yield call(async () => {
      try {
        const userPayload = await legacyGetPortalUserFn({
          userId,
          eventId: eventDetails.uuid
        });
        return userPayload;
      } catch (err) {
        throw err;
      }
    });

    // if empty user payload exists or no active view set, redirect to portal switcher
    if (!user || !user.active_view) {
      window.location = `/portal-switch/${eventDetails.slug}/${eventDetails.uuid}`;
      return false;
    }
    return true;
  } catch (e) {
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred retrieving portal user"
        }
      ])
    );
    return false;
  }
};

const init = function*({ payload: { selectedCategory, getPortalUser } }) {
  // set ini category based on incoming route
  yield put(
    actions.setSelectedCategory({
      category: selectedCategory,
      ignoreRedirect: true
    })
  );

  yield put(actions.setLoading(true));

  // fetch + hydrate portal user data
  const success = yield call(refreshUserData, {
    legacyGetPortalUserFn: getPortalUser
  });

  // if successful, fetch portal data
  if (success) {
    yield call(refreshData);
    actions.setLoading(false);
  }
};

const cleanSections = function*() {
  const selectedCategory = yield select(getters.selectedCategory);
  yield put(
    SessionFlyOverActions.setFromHomePage(
      SECTIONS_IDS.HOME === selectedCategory
    )
  );
  yield put(SessionFlyOverActions.closeSessionFlyover());
};

const setSelectedCategory = function*({
  payload: { category, ignoreRedirect = false }
}) {
  const eventDetails = yield select(getEventDetails);
  yield call(cleanSections);

  if (ignoreRedirect === false) {
    yield put(
      push(
        `/portals/${eventDetails.slug}/${eventDetails.uuid}${
          category === SECTIONS_IDS.HOME ? `` : `/${category}`
        }`
      )
    );
  }
};

const goHomeFromWikiPage = function*() {
  yield put(actions.setSelectedCategory({ category: SECTIONS_IDS.HOME }));
  yield put(actions.setShouldScrollToSection(SECTIONS_IDS.RESOURCES));
};

const goToWikiPage = function*({ payload: resourceId }) {
  if (!resourceId) {
    yield put(actions.setSelectedCategory({ category: SECTIONS_IDS.HOME }));
    return true;
  }

  const eventDetails = yield select(getEventDetails);
  yield call(cleanSections);
  yield put(
    push(
      `/portals/${eventDetails.slug}/${eventDetails.uuid}/resources/${resourceId}`
    )
  );

  return true;
};

const navigateTo = function*({ payload: { id, shouldScrollToSection } }) {
  const isInHome = yield select(getters.selectedCategory) === SECTIONS_IDS.HOME;
  if (shouldScrollToSection) {
    yield put(actions.setShouldScrollToSection(id));
    if (!isInHome) {
      yield put(actions.setSelectedCategory({ category: SECTIONS_IDS.HOME }));
    }
  } else {
    yield put(actions.setSelectedCategory({ category: id }));
  }
};

const watchSetActiveWikiPage = function*() {
  yield takeEvery(actions.setActiveWikiPage.type, goToWikiPage);
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchGoHomeFromWikiPage = function*() {
  yield takeEvery(actions.goHomeFromWikiPage.type, goHomeFromWikiPage);
};

const watchFlyoverRefeshData = function*() {
  yield takeEvery(TaskFlyOverActions.refreshData.type, refreshData);
};

const watchFlyoverSessionRefeshData = function*() {
  yield takeEvery(SessionFlyOverActions.refreshData.type, refreshData);
};

const watchSetSelectedCategory = function*() {
  yield takeEvery(actions.setSelectedCategory.type, setSelectedCategory);
};

const watchNavigateTo = function*() {
  yield takeEvery(actions.navigateTo.type, navigateTo);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchGoHomeFromWikiPage),
    fork(watchSetActiveWikiPage),
    fork(watchFlyoverRefeshData),
    fork(watchFlyoverSessionRefeshData),
    fork(watchSetSelectedCategory),
    fork(watchNavigateTo)
  ]);
};

export default rootSaga;
